import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
import {RINGBA_DNI_PHONE, formatPhoneNumber} from "@/lib/constants";
export const meta = {
  title: "What is the 80% Rule in Home Insurance?",
  description: "Understand the 80% rule in home insurance to avoid coinsurance penalties and ensure adequate coverage for your property.",
  category: "General",
  tags: [],
  featuredImage: {
    src: "/images/blog/AdobeStock_1470642828-scaled.webp",
    alt: "What is the 80% Rule in Home Insurance?"
  },
  author: {
    name: "Dash Lewis, Kara Credle",
    role: "Author",
    image: ""
  },
  publishedAt: "July 24, 2025",
  updatedAt: "July 30, 2025",
  showTOC: true
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    em: "em",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    hr: "hr",
    img: "img",
    li: "li",
    ol: "ol",
    p: "p",
    strong: "strong",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      children: meta.title
    }), "\n", meta.description, "\n", _jsx(_components.hr, {}), "\n", _jsxs(_components.p, {
      children: ["Homeowners insurance may not be required by law, but it helps protect you in the event of damage or disaster. Being underinsured today can lead to severe financial stress in the future as natural disasters like wildfires and floods become more frequent. Too many people have too little ", _jsx(_components.a, {
        href: "/insurance/home",
        children: "home insurance"
      }), ". According to the Insurance Information Institute, between ", _jsx(_components.a, {
        href: "https://uphelp.org/two-thirds-of-homeowners-underinsured-for-wildfire-loss/",
        children: "20% and 60%"
      }), " of homeowners don’t carry enough coverage. This coverage gap is exacerbated by the fact that ", _jsx(_components.a, {
        href: "/home-insurance/homeowners-insurance-knowledge-survey",
        children: "50% of homeowners"
      }), " aren’t exactly sure what is and isn’t covered by their own policy."]
    }), "\n", _jsx(_components.p, {
      children: "To make sure you have the best safety net possible, keep your coverage at least 80% of your home’s total replacement cost to meet what’s known as the 80% coinsurance rule. We’ll explain the ins and outs of the 80% rule and offer some tips to help you assess whether or not you’re adequately covered."
    }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
      children: "Key Takeaways"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["\n", _jsx(_components.p, {
          children: "The 80% rule in home insurance is a standard that recommends homeowners carry insurance coverage of at least 80% of a home’s total replacement cost."
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["\n", _jsx(_components.p, {
          children: "Failing to meet this threshold results in proportional claim reductions, meaning if you only have 70% of required coverage, insurers may only pay 70% of any claim, leaving you responsible for the difference."
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["\n", _jsx(_components.p, {
          children: "To ensure you have adequate coverage, it’s recommended that you increase coverage limits after making any improvements to your home or adding additional structures to your property."
        }), "\n"]
      }), "\n"]
    }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
      children: "What Is the 80% Rule in Homeowners Insurance?"
    }), "\n", _jsxs(_components.p, {
      children: ["The 80% rule is also known as the “80/20 coinsurance rule.” ", _jsx(_components.a, {
        href: "https://sc.edu/study/colleges_schools/moore/directory/hartwig_robert.php",
        children: "Robert Hartwig"
      }), ", Clinical Associate Professor of Finance and Director of the Center for Risk and Uncertainty Management at the University of South Carolina, explains what 80% coinsurance means to the average homeowner."]
    }), "\n", _jsx(_components.p, {
      children: "“The ‘80/20’ rule is an informal name for what is generally referred to as the ‘coinsurance clause’ or ‘coinsurance requirement’ found in many property insurance policies,” he says. “The clause requires the homeowner to maintain insurance in an amount equal to or greater than a stated percentage of the replacement value of the property—in this case 80%.”"
    }), "\n", _jsx(_components.p, {
      children: "This means that you’ll need to reappraise your home periodically to determine its replacement cost and adjust the amount of dwelling coverage you carry. Factors like inflation, for example, can lead to higher replacement costs, and if inflation rises quickly, you may not be sufficiently covered at your current levels."
    }), "\n", _jsx(_components.h4, {
      children: "How Do You Calculate Total Replacement Cost?"
    }), "\n", _jsxs(_components.p, {
      children: ["To avoid underinsurance, ", _jsx(_components.a, {
        href: "https://business.mercer.edu/faculty-and-insights/faculty-directory/john-wilson-gordon/",
        children: "John Gordon"
      }), ", Director of Operations at Mercer University, advises “Homeowners should consider the home’s current replacement cost—including materials, labor, and local construction trends—rather than its market value.” It’s important to differentiate between the total replacement cost of your home and its actual cash value (ACV). Your home’s ACV takes depreciation into account, meaning it decreases with age. Depreciation refers to the loss of value over time. The total replacement cost, on the other hand, tends to rise due to various factors, the most important of which is inflation."]
    }), "\n", _jsxs(_components.p, {
      children: ["To ", _jsx(_components.a, {
        href: "https://www.experian.com/blogs/ask-experian/how-to-calculate-home-replacement-cost/",
        children: "calculate the total replacement cost"
      }), " of your home, you’ll first need to get an appraisal. There are kinds of appraisals to choose from:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Insurance appraisal."
        }), " This type of appraisal is typically conducted by the insurance company, where an appraiser employed by the provider will collect information and take photos of your home, then decide the amount of coverage you should purchase."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "General appraisal."
        }), " You’re likely to get a more accurate number from a professional appraiser, who also collects the same pertinent information and takes photos, but also researches the area and factors in current market trends. These appraisals are usually conducted for real estate purposes, but getting one can help you learn your home’s replacement cost."]
      }), "\n"]
    }), "\n", _jsx(_components.h4, {
      children: "Factors That Influence Replacement Cost"
    }), "\n", _jsx(_components.p, {
      children: "Here are some of the factors that affect your home’s replacement cost:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Inflation."
        }), " If inflation is high, it affects the cost of everything — building materials, labor costs, interest rates, and more. The higher the inflation rate, the higher your home’s replacement cost will be."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Upgrades, renovations, and improvements."
        }), " If you’ve recently upgraded or renovated any part of your house — updating your appliances, changing the flooring, installing new windows — your home’s replacement cost will be higher."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Location."
        }), " Homes in dense urban areas will cost more to replace than homes in rural areas."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Capital improvements."
        }), " Adding a new permanent structure, such as a garage or shed, increases the overall replacement cost of your property and may require additional coverage."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "The overall housing market."
        }), " You shouldn’t use your home’s actual cash value when determining the amount of coverage you need, but your home’s market value will affect its replacement cost."]
      }), "\n"]
    }), "\n", _jsxs(_components.h3, {
      children: ["What Do You Risk if You Don't Meet the 80% Rule?", _jsx(_components.img, {
        src: "/images/blog/AdobeStock_428391843-1024x683.webp",
        alt: "Home with damage showing the risk of not meeting the 80 percent insurance rule"
      })]
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.em, {
        children: "Credit: Adobe – Royalty Free"
      })
    }), "\n", _jsx(_components.p, {
      children: "If you don’t meet the 80% rule, you run the risk of being underinsured, which means you don’t have enough coverage to replace or repair your property in the event of an accident or disaster. Underinsured homeowners may receive low payouts from insurers when filing a claim."
    }), "\n", _jsxs(_components.p, {
      children: ["“In the event of a partial loss, the claim payout may be reduced proportionally based on the amount of coverage the homeowner actually has compared to what they should have had (80% of the cost to rebuild),” says ", _jsx(_components.a, {
        href: "https://culverhouse.ua.edu/people/boyi-zhuang/",
        children: "Boyi Zhuang"
      }), ", Associate Researcher at the Center for Risk and Insurance Research at the University of Alabama. “This reduction could leave the homeowner responsible for a significant portion of the repair or rebuilding costs.”"]
    }), "\n", _jsx(_components.p, {
      children: "Let’s say, for instance, that the current replacement cost of your home is $475,000. If you follow the 80% coinsurance rule, you should have a coverage limit of at least $380,000. If you haven’t reassessed your home insurance policy in several years, you may only have $300,000 worth of coverage, which is about 63% of your replacement cost and 79% of the $380,000 needed to reach the 80% rule."
    }), "\n", _jsx(_components.p, {
      children: "This means that if you file a claim, the insurance company may only pay out 79% of your claim. If you file a $60,000 claim, the insurer would only pay $47,400, leaving you on the hook for the remaining $12,600. That difference is called a coinsurance penalty, which refers to the amount you’ll have to pay due to insufficient coverage. “In the event of a partial loss, the insurer may apply a coinsurance penalty, leaving the homeowner responsible for a portion of repair or replacement costs that could otherwise have been covered” explains Gordon. The more you have to pay out-of-pocket, the more likely it is that you’ll have to slow walk restoration, potentially forgoing certain repairs altogether."
    }), "\n", _jsx(_components.h3, {
      children: "Tips for Ensuring Adequate Coverage"
    }), "\n", _jsx(_components.p, {
      children: "There are steps you can take to ensure you have coverage that complies with the 80% coinsurance clause. If you get an appraisal, consult with the appraiser to make sure you have a thorough understanding of your home’s replacement cost. Robert Harwig stresses the importance of having an up-to-date assessment of the replacement cost. “The amount of insurance purchased should be increased as building costs rise and as improvements to the home are made, both of which will increase repair and rebuilding costs,” he says."
    }), "\n", _jsx(_components.p, {
      children: "Talking to your insurance agent is another crucial step to make sure your coverage is adequate. Boyi Zhuang recommends speaking to your insurance agent at least once a year and after any renovations or upgrades. Together, you can adjust your coverage levels to ensure you meet the 80% rule."
    }), "\n", _jsxs(_components.p, {
      children: ["Here are some questions to ", _jsx(_components.a, {
        href: "https://www.ncdoi.gov/consumers/homeowners-insurance/questions-ask-your-agent",
        children: "ask your insurance agent"
      }), ":"]
    }), "\n", _jsxs(_components.ol, {
      children: ["\n", _jsx(_components.li, {
        children: _jsx(_components.strong, {
          children: "Do I have enough insurance to rebuild my home if it is destroyed?"
        })
      }), "\n", _jsx(_components.li, {
        children: _jsx(_components.strong, {
          children: "Do I need flood and earthquake insurance? What would be the cost?"
        })
      }), "\n", _jsx(_components.li, {
        children: _jsx(_components.strong, {
          children: "Does this policy cover water damage, including damage from sewer, drain, or sump pump backup?"
        })
      }), "\n", _jsx(_components.li, {
        children: _jsx(_components.strong, {
          children: "Do I have enough coverage to replace all of my belongings?"
        })
      }), "\n", _jsx(_components.li, {
        children: _jsx(_components.strong, {
          children: "Should I increase my coverage limits over time to account for updates I make to my home?"
        })
      }), "\n"]
    }), "\n", _jsx(_components.h3, {
      children: "Bottom Line"
    }), "\n", _jsx(_components.p, {
      children: "To protect your financial well-being and ensure adequate insurance coverage, it’s crucial to understand the 80% coinsurance rule. You should hold coverage of at least 80% of the total replacement cost of your home. If you hold less coverage, you may face higher out-of-pocket expenses in the event of an accident or disaster, adding to an already stressful time. Experts suggest consulting with your insurance agent at least once a year to reassess your coverage."
    }), "\n", _jsxs(_components.p, {
      children: ["If you’re unsure of how to calculate your home’s total replacement cost or need more information about how to meet the 80%, call Guardian Service at ", formatPhoneNumber(RINGBA_DNI_PHONE), ". Our zero-commission agents can help you determine the right amount of coverage to purchase, so you can rest assured you’re protected when disaster strikes."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "About Guardian Service"
      }), ": We’re an independent insurance agency based in Raleigh, North Carolina, reimagining the insurance experience for homeowners. Our agents aren’t commissioned (seriously — zero commission), and our entire model is based on getting the right coverage for each person by continually re-shopping across 50+ carriers. It’s insurance designed to adapt to homeowners, not the other way around."]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
